import React from 'react';
import { Tooltip, Box, Typography, Stack } from '@mui/material';
import { RiskAnalysis } from '@/services/product-catalogue/pc-api';
import { RiskChip } from '../risk-chip';
import { tokens } from "@/tokens";

interface RiskAnalysisTooltipProps {
  children: React.ReactElement;
  riskAnalysis: RiskAnalysis;
}

export const RiskAnalysisTooltip: React.FC<RiskAnalysisTooltipProps> = ({ 
  children, 
  riskAnalysis 
}) => {
  const formatAnalyzedAt = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const tooltipContent = (
    <Box sx={{ maxWidth: 300, p: 1 }}>
      <Stack spacing={1.5}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="body2" fontWeight={tokens.fontWeight.bold} color="inherit">
            AI Risk Analysis
          </Typography>
          <RiskChip 
            riskLevel={riskAnalysis.riskLevel} 
            size="small" 
          />
        </Stack>
        
        {riskAnalysis.riskLevel === 'RUNNING' || riskAnalysis.riskLevel === 'PENDING' ? (
          <Typography variant="body2" color="inherit" sx={{ lineHeight: 1.4 }}>
            {riskAnalysis.riskLevel === 'RUNNING' 
              ? 'Analysis is currently in progress...' 
              : 'Analysis is pending and will start soon...'}
          </Typography>
        ) : (
          <Typography variant="body2" color="inherit" sx={{ lineHeight: 1.4 }}>
            {riskAnalysis.riskReason}
          </Typography>
        )}
        
        {(riskAnalysis.riskLevel !== 'RUNNING' && riskAnalysis.riskLevel !== 'PENDING') && (
          <Box sx={{ pt: 0.5, borderTop: '1px solid rgba(255, 255, 255, 0.2)' }}>
            <Typography variant="caption" color="inherit" sx={{ opacity: 0.8 }}>
              Analyzed: {formatAnalyzedAt(riskAnalysis.analyzedAt)}
            </Typography>
            {riskAnalysis.analysisVersion && (
              <Typography variant="caption" color="inherit" sx={{ opacity: 0.8, display: 'block' }}>
                Version: {riskAnalysis.analysisVersion}
              </Typography>
            )}
          </Box>
        )}
      </Stack>
    </Box>
  );

  return (
    <Tooltip
      title={tooltipContent}
      placement="top"
      arrow
      slotProps={{
        popper: {
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, -8],
              },
            },
          ],
        },
        tooltip: {
          sx: {
            bgcolor: '#1e293b',
            color: 'white',
            fontSize: tokens.fontSize.lg,
            borderRadius: tokens.radius.md,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
          },
        },
      }}
    >
      {children}
    </Tooltip>
  );
};

export default RiskAnalysisTooltip;